// TrafficTracker — fires a privacy-friendly pageview beacon on public
// marketing-page route changes. Staff visits are excluded server-side
// (cb_admin_session cookie). No cookies set for visitors; a random
// localStorage id de-dupes uniques.
//
// Also captures full ad attribution (utm_*, gclid, fbclid, msclkid) and
// persists FIRST-TOUCH in localStorage via lib/adAttribution.js so we can
// attribute later conversions back to the ad that brought them in.
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { captureAdAttribution, AD_ATTR_KEYS } from '../lib/adAttribution';

const API = process.env.REACT_APP_BACKEND_URL;

const TRACKED_PATHS = new Set([
  '/', '/home',
  // Ad-group-specific landing variants — each is tracked separately so we
  // can compare conversion rate per creative in owner-admin traffic.
  '/home-cpa', '/home-bookkeeping', '/home-quickbooks',
  '/elite', '/landing-classic', '/legacy-landing', '/demo',
  '/features', '/features-classic', '/resources', '/contact',
  '/security', '/welcome', '/login', '/privacy', '/terms', '/portals/find',
]);

function getVisitorId() {
  try {
    let vid = localStorage.getItem('cb_vid');
    if (!vid) {
      vid = `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem('cb_vid', vid);
    }
    return vid;
  } catch {
    return 'v_anon';
  }
}

export default function TrafficTracker() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    if (!TRACKED_PATHS.has(path) || lastPath.current === path) return;
    lastPath.current = path;

    // Capture first-touch ad attribution (writes to localStorage on new
    // signals only). The returned blob is the attribution CONTEXT for this
    // pageview — either the fresh URL params or the previously-stored one.
    const attr = captureAdAttribution({ landingPath: path });

    const payload = {
      path,
      referrer: document.referrer || null,
      visitor_id: getVisitorId(),
    };
    // Only send keys that have a value — keeps the beacon small and lets
    // the backend distinguish "not provided" from "explicitly empty".
    for (const k of AD_ATTR_KEYS) {
      if (attr[k]) payload[k] = attr[k];
    }

    fetch(`${API}/api/analytics/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  }, [location.pathname]);

  return null;
}
